import { Text, Button, Stack, Box, useTheme } from '@ltvco/refresh-lib/theme';

import {
  JurisdictionModalStyled,
  ModalContentContainer,
  ModalBody,
  ModalFooter,
} from '../../component';

interface PacerRedirectModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  pacerUrl?: string;
}

export const PacerRedirectModal = ({
  open,
  onClose,
  onContinue,
}: PacerRedirectModalProps) => {
  const theme = useTheme();

  return (
    <JurisdictionModalStyled
      open={open}
      onClose={onClose}
      hasCloseIcon
      sx={{
        '& .MuiDialog-paper': {
          width: '700px',
          height: 'auto',
          maxWidth: '90vw',
          maxHeight: 'none',
        },
      }}
    >
      <ModalContentContainer>
        {/* Header with custom styling for better mobile appearance */}
        <Box
          sx={{
            position: 'relative',
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 2,
            pt: { xs: 4, sm: 2 },
            px: { xs: 2, sm: 3 },
            mb: 2,
          }}
        >
          <Text
            variant="h2"
            sx={{
              width: '100%',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
              lineHeight: { xs: 1.3, sm: 1.4 },
              px: { xs: 2, sm: 0 },
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            Leaving BeenVerified to Purchase on PACER
          </Text>
        </Box>

        {/* Body */}
        <ModalBody sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
          <Text
            variant="body1"
            sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            You're about to be redirected to PACER (Public Access to Court
            Electronic Records), the official U.S. federal court system.
          </Text>
          <Text
            variant="body1"
            sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            Some court documents are not available for download and must be
            purchased directly through PACER. BeenVerified provides download
            access whenever possible, but if a document hasn't been added to the
            public archive, PACER requires a small fee to download it.
          </Text>
          <Text
            variant="body1"
            sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            Even if a free version exists, you may still want to purchase on
            PACER to ensure you’re getting the latest and most complete version
            of the filing.
          </Text>
        </ModalBody>

        {/* Footer with buttons */}
        <ModalFooter>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ width: '100%' }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius: '30px',
                px: 3,
                py: 1.5,
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                minWidth: '160px',
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: '30px',
                px: 3,
                py: 1.5,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                minWidth: '160px',
                width: { xs: '100%', sm: 'auto' },
              }}
              onClick={onContinue}
            >
              Continue to PACER
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContentContainer>
    </JurisdictionModalStyled>
  );
};
